import React, { useState, useCallback } from 'react';
import PropTypes from 'prop-types';

import { getFoldersLimitPerAccount } from '@bingads-webui-campaign-react/ads-studio-app-shared-components';

import { EditFolderNameModal } from '../components/edit-folder-name-modal';

const CreateFolderModal = ({
  isDialogVisible,
  setDialogVisible,
  createFolderAsync,
  onSuccess,
  onError,
  aid,
  i18n,
  logger,
}) => {
  const [errorMessage, setErrorMessage] = useState('');

  const closeDialog = useCallback(() => {
    setDialogVisible(false);
    setErrorMessage('');
  }, [setDialogVisible]);

  const onCreateFolder = useCallback((name) => {
    logger.logTrace('Confirm to create folder', { folderName: name });
    createFolderAsync({ folderName: name }).then(() => {
      setDialogVisible(false);
      onSuccess(name);
      logger.logTrace('Create folder successfully', { folderName: name });
    }).catch((alODataErr) => {
      const { actionable, getUXMessage } = alODataErr;
      if (actionable) {
        // prompt user to correct
        setErrorMessage(getUXMessage(i18n));
      } else {
        const getParams = () => ({ value: getFoldersLimitPerAccount(aid, logger) });
        const localizedUXMessage = getUXMessage(i18n, getParams);
        closeDialog();
        logger.logError(alODataErr, { folderName: name });
        onError(localizedUXMessage);
      }
    });
  }, [logger, createFolderAsync, setDialogVisible, onSuccess, i18n, closeDialog, onError, aid]);

  const onCancel = useCallback(() => {
    closeDialog();
    logger.logTrace('Cancel to create folder');
  }, [closeDialog, logger]);

  return (
    <EditFolderNameModal
      show={isDialogVisible}
      i18n={i18n}
      onComplete={onCreateFolder}
      onCancel={onCancel}
      errorText={errorMessage}
      setErrorText={setErrorMessage}
    />
  );
};

CreateFolderModal.propTypes = {
  isDialogVisible: PropTypes.bool.isRequired,
  setDialogVisible: PropTypes.func.isRequired,
  createFolderAsync: PropTypes.func.isRequired,
  aid: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  onSuccess: PropTypes.func.isRequired,
  onError: PropTypes.func.isRequired,
  i18n: PropTypes.shape({
    getString: PropTypes.func,
  }).isRequired,
  logger: PropTypes.shape({
    logTrace: PropTypes.func,
    logError: PropTypes.func,
  }).isRequired,
};

export const useCreateFolderModal = (aid, i18n, logger) => {
  const [isDialogVisible, setDialogVisible] = useState(false);

  const openModal = () => {
    setDialogVisible(true);
  };

  const render = useCallback((createFolderAsync, onSuccess, onError) => (<CreateFolderModal
    isDialogVisible={isDialogVisible}
    setDialogVisible={setDialogVisible}
    createFolderAsync={createFolderAsync}
    onSuccess={onSuccess}
    onError={onError}
    aid={aid}
    i18n={i18n}
    logger={logger}
  />), [aid, i18n, isDialogVisible, logger]);

  return {
    open: openModal,
    render,
  };
};
